import logger from '../../utils/logger';

export const TokenService = {
  // Token storage keys
  ACCESS_TOKEN_KEY: 'access_token',
  REFRESH_TOKEN_KEY: 'refresh_token',
  TEACHER_ID_KEY: 'teacher_id',
  LOGIN_METHOD_KEY: 'login_method',
  
  setTokens: (accessToken, refreshToken, teacherId = null, loginMethod = 'password') => {
    try {
      localStorage.setItem(TokenService.ACCESS_TOKEN_KEY, accessToken);
      if (refreshToken) {
        localStorage.setItem(TokenService.REFRESH_TOKEN_KEY, refreshToken);
      }
      
      if (!teacherId && accessToken) {
        const userData = TokenService.getUserFromToken(accessToken);
        if (userData?.teacherId) {
          localStorage.setItem(TokenService.TEACHER_ID_KEY, userData.teacherId.toString());
        } else {
          logger.warn('Cannot store teacher ID: teacherId not found in token', {
            hasUserId: !!userData?.id,
            availableFields: userData ? Object.keys(userData) : []
          });
        }
      } else if (teacherId) {
        localStorage.setItem(TokenService.TEACHER_ID_KEY, teacherId.toString());
      }
      
      localStorage.setItem(TokenService.LOGIN_METHOD_KEY, loginMethod);
      logger.debug('Login method stored', { method: loginMethod });
    } catch (error) {
      logger.error('Failed to store tokens', error);
    }
  },
  
  // Get access token from localStorage
  getAccessToken: () => {
    try {
      const token = localStorage.getItem(TokenService.ACCESS_TOKEN_KEY);
      if (token && typeof token === 'string' && token.split('.').length === 3) {
        return token;
      }
      return null;
    } catch (error) {
      logger.error('Failed to retrieve access token', error);
      return null;
    }
  },

  // Get refresh token from localStorage
  getRefreshToken: () => {
    try {
      const token = localStorage.getItem(TokenService.REFRESH_TOKEN_KEY);
      if (token && typeof token === 'string' && token.split('.').length === 3) {
        return token;
      }
      return null;
    } catch (error) {
      logger.error('Failed to retrieve refresh token', error);
      return null;
    }
  },

  
  // Remove all tokens and teacherId from localStorage
  removeTokens: () => {
    try {
      localStorage.removeItem(TokenService.ACCESS_TOKEN_KEY);
      localStorage.removeItem(TokenService.REFRESH_TOKEN_KEY);
      localStorage.removeItem(TokenService.TEACHER_ID_KEY);
      localStorage.removeItem(TokenService.LOGIN_METHOD_KEY);
    } catch (error) {
      logger.error('Failed to remove tokens', error);
    }
  },

  // Clean up corrupted or invalid tokens
  cleanupInvalidTokens: () => {
    try {
      const accessToken = localStorage.getItem(TokenService.ACCESS_TOKEN_KEY);
      const refreshToken = localStorage.getItem(TokenService.REFRESH_TOKEN_KEY);
      
      // Check access token
      if (accessToken && (!TokenService.isValidToken(accessToken) || accessToken.split('.').length !== 3)) {
        logger.warn('Removing invalid access token');
        localStorage.removeItem(TokenService.ACCESS_TOKEN_KEY);
      }
      
      // Check refresh token
      if (refreshToken && (!TokenService.isValidToken(refreshToken) || refreshToken.split('.').length !== 3)) {
        logger.warn('Removing invalid refresh token');
        localStorage.removeItem(TokenService.REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      logger.error('Failed to cleanup invalid tokens', error);
      TokenService.removeTokens();
    }
  },
  
  // Check if access token exists
  hasAccessToken: () => {
    return !!TokenService.getAccessToken();
  },

  // Check if refresh token exists
  hasRefreshToken: () => {
    return !!TokenService.getRefreshToken();
  },
  
  // Validate token format and expiration
  isValidToken: (token) => {
    if (!token) return false;
    
    try {
      // Basic JWT format check (3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      
      // Check if token has expiration and if it's still valid
      if (payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp < currentTime) {
          return false; // Token expired
        }
      }
      
      return true;
    } catch (error) {
      logger.warn('Token validation error', error);
      return false;
    }
  },
  
  // Get token payload (decoded)
  getTokenPayload: (token = null) => {
    const tokenToUse = token || TokenService.getAccessToken();
    
    if (!tokenToUse) return null;
    
    try {
      const parts = tokenToUse.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      logger.warn('Failed to decode token payload', error);
      return null;
    }
  },
  
  // Get token expiration date
  getTokenExpiration: (token = null) => {
    const payload = TokenService.getTokenPayload(token);
    
    if (payload && payload.exp) {
      return new Date(payload.exp * 1000);
    }
    
    return null;
  },
  
  // Check if token will expire within specified minutes
  willExpireSoon: (minutes = 5, token = null) => {
    const expiration = TokenService.getTokenExpiration(token);
    
    if (!expiration) return false;
    
    const warningTime = new Date();
    warningTime.setMinutes(warningTime.getMinutes() + minutes);
    
    return expiration <= warningTime;
  },
  
  // Get user information from token 
  getUserFromToken: (token = null) => {
    const payload = TokenService.getTokenPayload(token);
    
    if (!payload) {
      logger.warn('No token payload available');
      return null;
    }
    
    if (!payload.teacherId && !payload.userId && !payload.sub && !payload.id) {
      logger.warn('Token payload missing user identification fields');
      return null;
    }
    
    const extractedId = payload.id || payload.userId || payload.sub;
    const extractedTeacherId = payload.teacherId; // Teacher-only app should have teacherId in token
    
    const userData = {
      id: extractedId,
      username: payload.username,
      email: payload.email,
      fullName: payload.fullname || payload.fullnameEn || payload.name,
      role: payload.role || payload.userRole,
      isActive: payload.isActive || payload.isActived,
      profileImage: payload.profileImage,
      staffId: payload.staffId,
      teacherId: extractedTeacherId,
      permissions: payload.permissions || []
    };

    // Log for debugging in development
    logger.debug('Extracted user data from token', {
      hasTeacherId: !!userData.teacherId,
      hasId: !!userData.id,
      teacherId: userData.teacherId,
      username: userData.username,
      email: userData.email,
      payloadKeys: Object.keys(payload)
    });

    // Warn if teacherId is missing in teacher-only app
    if (!userData.teacherId) {
      logger.warn('Teacher ID missing from token in teacher-only app', {
        availableFields: Object.keys(payload),
        extractedId: extractedId,
        hasUserId: !!payload.userId,
        hasId: !!payload.id
      });
    }

    return userData;
  },

  getTeacherId: () => {
    try {
      const teacherId = localStorage.getItem(TokenService.TEACHER_ID_KEY);
      if (teacherId && !isNaN(teacherId)) {
        return parseInt(teacherId, 10);
      }
      
      const userData = TokenService.getUserFromToken();
      if (userData?.teacherId) {
        localStorage.setItem(TokenService.TEACHER_ID_KEY, userData.teacherId.toString());
        return userData.teacherId;
      }
      
      // For teacher-only app, we should not fall back to user.id
      if (userData?.id && !userData?.teacherId) {
        logger.warn('Teacher ID not found in token, but user.id available. This should not happen in teacher-only app.', {
          userId: userData.id,
          tokenFields: Object.keys(userData)
        });
      }
      
      return null;
    } catch (error) {
      logger.error('Failed to retrieve teacherId', error);
      return null;
    }
  },

  setTeacherId: (teacherId) => {
    try {
      if (teacherId) {
        localStorage.setItem(TokenService.TEACHER_ID_KEY, teacherId.toString());
      }
    } catch (error) {
      logger.error('Failed to store teacherId', error);
    }
  },

  hasTeacherId: () => {
    const teacherId = TokenService.getTeacherId();
    return !!teacherId;
  },

  getLoginMethod: () => {
    try {
      const method = localStorage.getItem(TokenService.LOGIN_METHOD_KEY);
      return method || 'password';
    } catch (error) {
      logger.error('Failed to retrieve login method', error);
      return 'password';
    }
  },

  setLoginMethod: (method) => {
    try {
      const validMethods = ['password', 'liff'];
      if (!validMethods.includes(method)) {
        logger.warn('Invalid login method provided', { method });
        return;
      }
      localStorage.setItem(TokenService.LOGIN_METHOD_KEY, method);
      logger.debug('Login method updated', { method });
    } catch (error) {
      logger.error('Failed to store login method', error);
    }
  },

  isLiffLogin: () => {
    return TokenService.getLoginMethod() === 'liff';
  },

  isPasswordLogin: () => {
    return TokenService.getLoginMethod() === 'password';
  }
};